import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';

import { skip, Subscription } from 'rxjs';

import { MobilePanSearchService } from '../../../services/mobile-pan/mobile-pan.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';

@Component({
  selector: 'app-osint-mobile-pan-search-result',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    HeaderComponent,
    SidebarComponent,
  ],
  templateUrl: './osint-mobile-pan-search-result.component.html',
  styleUrl: './osint-mobile-pan-search-result.component.css',
})
export class OsintMobilePanSearchResultComponent implements OnInit, OnDestroy {
  jobId: string = '';
  searchId: string = '';
  selectedSearchResult: any;
  mobilePanData: any = null;
  noRecords = false;

  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;

  jobResultSubscription?: Subscription;

  get isLoadingResults(): boolean {
    const hasResults = !!this.mobilePanData;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults &&
      !this.noRecords
    );
  }

  notes: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private searchPollingService: SearchPollingService,
    private mobilePanSearchService: MobilePanSearchService,
    public utilsService: UtilsService,
  ) {}

  ngOnInit() {
    this.selectedSearchResult = history.state?.searchResult;
    this.jobId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.selectedSearchResult && !this.jobId) {
      this.router.navigate(['/mobile-pan-search']);
      return;
    }

    if (this.selectedSearchResult) {
      this.searchId = this.selectedSearchResult.mobile_pan_search_id;

      if (this.selectedSearchResult.done) {
        this.getCompletedSearchResult();
      } else {
        this.pollForSearchResult();
      }
    } else if (this.jobId) {
      this.pollForSearchResult();
    }
  }

  ngOnDestroy() {
    this.jobResultSubscription?.unsubscribe();
  }

  onBackButtonClick() {
    this.router.navigate(['/mobile-pan-search']);
  }

  getCompletedSearchResult() {
    if (!this.searchId) {
      return;
    }

    this.loadingCompletedSearchResult = true;

    this.mobilePanSearchService.getCompletedSearchResult(this.searchId).subscribe({
      next: (response) => {
        const rawSearchResultData = response?.data;
        const completedSearchResult = rawSearchResultData?.data_list?.[0];

        if (!completedSearchResult) {
          this.noRecords = true;
          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
          return;
        }

        this.selectedSearchResult = {
          ...this.selectedSearchResult,
          ...completedSearchResult,
          searched_phone:
            completedSearchResult?.mobile_pan_data?.phone ||
            this.selectedSearchResult?.searched_phone,
        };

        this.notes = rawSearchResultData?.notes || completedSearchResult?.notes || '';
        this.mobilePanData =
          completedSearchResult?.mobile_pan_data || null;
        this.noRecords = !this.mobilePanData?.found && !this.hasAnyField(this.mobilePanData);

        this.loadingCompletedSearchResult = false;
        this.loadingPolledSearchResult = false;
      },
      error: (error) => {
        console.error(error);
        this.loadingCompletedSearchResult = false;
        this.loadingPolledSearchResult = false;
      },
    });
  }

  private hasAnyField(data: any): boolean {
    if (!data) return false;
    return Boolean(data.name || data.pan || data.dob || data.gender || data.phone);
  }

  pollForSearchResult() {
    if (!this.jobId) {
      this.jobId = this.selectedSearchResult?.job_id;
    }

    if (!this.jobId) {
      console.error('No job ID available for polling');
      return;
    }

    this.searchPollingService.startPolling([this.jobId], (ids) =>
      this.mobilePanSearchService.getSearchResults(ids),
    );

    this.jobResultSubscription?.unsubscribe();
    this.loadingPolledSearchResult = true;

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(this.jobId)
      .pipe(skip(1))
      .subscribe({
        next: (response) => {
          if (!response) {
            return;
          }

          if (response.mobile_pan_search_id && !this.searchId) {
            this.searchId = response.mobile_pan_search_id;
          }

          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            done: response.done || false,
            status: response.status || 'pending',
            searched_phone:
              response?.searched_phone || this.selectedSearchResult?.searched_phone,
            mobile_pan_search_id:
              response?.mobile_pan_search_id ||
              this.selectedSearchResult?.mobile_pan_search_id,
          };

          if (response.done === true || response.status === 'completed') {
            this.getCompletedSearchResult();
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingPolledSearchResult = false;
        },
      });
  }

  onUpdateNotesButtonClick(): void {
    const resultId = this.selectedSearchResult?.result_id;

    if (!this.notes.trim() || !resultId) {
      this.snackBar.open(
        'Please enter notes and ensure result ID is available.',
        'Close',
        {
          duration: 3000,
          panelClass: ['snackbar-error'],
        },
      );
      return;
    }

    const payload = {
      result_id: resultId,
      notes: this.notes.trim(),
    };

    this.mobilePanSearchService.updateMobilePanNotes(payload).subscribe({
      next: () => {
        this.snackBar.open('Notes updated successfully!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to update notes.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  formatPhone(phone: string | null | undefined): string {
    if (!phone) return '-';
    const trimmed = String(phone).trim();
    if (!trimmed) return '-';
    if (trimmed.startsWith('+')) return trimmed;

    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
    if (digits.length === 10) return `+91 ${digits}`;
    return trimmed;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      if (this.utilsService?.formatDateTime) {
        return this.utilsService.formatDateTime(dateString, 'MMM d, y');
      }
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }
}
